import { Card } from "@/components/ui/card";
import { TrendingUp, BarChart3, Target, Activity } from "lucide-react";
import type { Load, Truck as TruckType } from "@shared/schema";

interface AnalyticsDashboardProps {
  userLoads?: Load[];
  userTrucks?: TruckType[];
  claimedLoads?: Load[];
  userType: string;
}

export function AnalyticsDashboard({ userLoads, userTrucks, claimedLoads, userType }: AnalyticsDashboardProps) {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const recentLoads = userLoads?.filter(l => new Date(l.createdAt) >= thirtyDaysAgo) || [];
  const recentTrucks = userTrucks?.filter(t => new Date(t.createdAt) >= thirtyDaysAgo) || [];

  const totalLoads = userLoads?.length || 0;
  const completedLoads = userLoads?.filter(l => l.status === 'Completed').length || 0;
  const claimedCompletedLoads = claimedLoads?.filter(l => l.status === 'Completed').length || 0;
  
  const totalCompleted = completedLoads + claimedCompletedLoads;
  const totalTransactions = totalLoads + (claimedLoads?.length || 0);
  const completionRate = totalTransactions > 0 ? Math.round((totalCompleted / totalTransactions) * 100) : 0;

  const getActivityLabel = () => {
    if (userType === 'Shipper') return 'Loads Posted (30 days)';
    if (userType === 'Carrier') return 'Trucks Posted (30 days)';
    return 'Listings Posted (30 days)';
  };

  const getActivityCount = () => {
    if (userType === 'Shipper') return recentLoads.length;
    if (userType === 'Carrier') return recentTrucks.length;
    return recentLoads.length + recentTrucks.length;
  };

  const getInProgressCount = () => {
    const loadsInProgress = userLoads?.filter(l => l.status === 'Confirmed' || (l.claimedByUserId && l.status !== 'Completed')).length || 0;
    const claimedInProgress = claimedLoads?.filter(l => l.status !== 'Completed').length || 0;
    return loadsInProgress + claimedInProgress;
  };

  return (
    <Card className="p-4 mb-8" data-testid="card-analytics">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-primary" />
        <h3 className="font-semibold text-lg">Analytics Dashboard</h3>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 bg-muted/50 rounded-lg" data-testid="analytics-activity">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{getActivityLabel()}</span>
          </div>
          <p className="text-xl font-bold">{getActivityCount()}</p>
        </div>

        <div className="p-3 bg-muted/50 rounded-lg" data-testid="analytics-completion-rate">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Completion Rate</span>
          </div>
          <p className="text-xl font-bold">{completionRate}%</p>
          <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-500" 
              style={{ width: `${completionRate}%` }}
            />
          </div>
        </div>

        <div className="p-3 bg-muted/50 rounded-lg" data-testid="analytics-in-progress">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">In Progress</span>
          </div>
          <p className="text-xl font-bold">{getInProgressCount()}</p>
        </div>

        <div className="p-3 bg-muted/50 rounded-lg" data-testid="analytics-total-completed">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Total Completed</span>
          </div>
          <p className="text-xl font-bold">{totalCompleted}</p>
        </div>
      </div>
    </Card>
  );
}
